/**
 * Activity detail — single payment-link page with timeline + actions.
 *
 * Header shows back + amount. Cards: status, details, action row.
 */

import { useEffect, useState } from "react";
import { Link, useParams } from "@tanstack/react-router";
import { ApiError, getInvoice, type Invoice } from "../lib/api";
import { Banner, Btn, KV, Pill } from "../components/ui";
import { Ic } from "../components/Icons";
import { formatUsd } from "../lib/format";

const truncMid = (s: string, head = 6, tail = 4): string =>
  s.length <= head + tail + 1 ? s : `${s.slice(0, head)}…${s.slice(-tail)}`;

const formatDateTime = (ms: number): string =>
  new Date(ms).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

// ─── V1.1: per-chain display helpers ─────────────────────────────────
// Kept in sync with backend src/server/checkout-page.ts. If they drift,
// docs/SHIELDZ_V1_1_ARCHITECTURE.md §5.2 is the contract.

const CHAIN_DECIMALS: Record<string, number> = {
  ZEC: 8, BTC: 8,
  BASE: 6, ARB: 6, OP: 6, POLY: 6, ETH: 6,
};
const CHAIN_TICKER: Record<string, string> = {
  ZEC: "ZEC", BTC: "BTC",
  BASE: "USDC", ARB: "USDC", OP: "USDC", POLY: "USDC", ETH: "USDC",
};
const CHAIN_LABEL: Record<string, string> = {
  ZEC: "Zcash",
  BTC: "Bitcoin",
  BASE: "Base USDC",
  ARB: "Arbitrum USDC",
  OP: "Optimism USDC",
  POLY: "Polygon USDC",
  ETH: "Ethereum USDC",
};

const formatChainAmount = (atomic: string, chainId: string): string => {
  const decimals = CHAIN_DECIMALS[chainId] ?? 6;
  // Big-int safe division — strip trailing zeros for readability.
  const neg = atomic.startsWith("-");
  const raw = neg ? atomic.slice(1) : atomic;
  const padded = raw.padStart(decimals + 1, "0");
  const intPart = padded.slice(0, padded.length - decimals);
  const fracPartFull = padded.slice(padded.length - decimals);
  const fracPart = fracPartFull.replace(/0+$/, "");
  const out = fracPart.length > 0 ? `${intPart}.${fracPart}` : intPart;
  return neg ? `-${out}` : out;
};

/** What the buyer typed into the payment link's questions (metadata.custom_fields). */
const buyerAnswers = (metadata: unknown): Array<{ label: string; value: string }> => {
  const f = (metadata as { custom_fields?: unknown } | null)?.custom_fields;
  return Array.isArray(f)
    ? f.filter((x): x is { label: string; value: string } => typeof x?.label === "string" && typeof x?.value === "string")
    : [];
};

export function ActivityDetail() {
  const { id } = useParams({ from: "/activity/$id" });
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const inv = await getInvoice(id);
        if (!cancelled) setInvoice(inv);
      } catch (e) {
        if (!cancelled) setError(e instanceof ApiError ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (error) {
    return (
      <div className="scroll">
        <div className="pad">
          <Banner tone="fail">{error}</Banner>
          <Link to="/activity" className="web-back" style={{ marginTop: 12 }}>
            <Ic name="back" size={16} /> Back to activity
          </Link>
        </div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="scroll">
        <div className="pad">
          <div className="muted" style={{ color: "var(--ink-3)" }}>Loading…</div>
        </div>
      </div>
    );
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(invoice.pay_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  };

  const handleShare = () => {
    const url = encodeURIComponent(invoice.pay_url);
    const text = encodeURIComponent(
      `Pay $${(invoice.amount_usd_cents / 100).toFixed(2)}${
        invoice.memo ? ` for ${invoice.memo}` : ""
      } via Shieldz`,
    );
    window.open(`https://t.me/share/url?url=${url}&text=${text}`, "_blank");
  };

  return (
    <div className="scroll">
      <div className="pad fade-in">
        <div className="card" style={{ padding: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <Pill status={invoice.status} />
            {invoice.paid_after_expiry && (
              <span className="pill warn">
                <span className="pd" />
                Paid after expiry
              </span>
            )}
          </div>
          <div
            className="tnum"
            style={{
              fontSize: 42,
              fontWeight: 600,
              letterSpacing: "-0.03em",
              marginBottom: 4,
            }}
          >
            ${(invoice.amount_usd_cents / 100).toFixed(2)}
          </div>
          {invoice.memo && (
            <div style={{ color: "var(--ink-2)", fontSize: 14 }}>
              {invoice.memo}
            </div>
          )}
        </div>

        {invoice.line_items && invoice.line_items.length > 0 && (
          <>
            <div className="s-label">Order</div>
            <div className="card card-pad">
              {invoice.line_items.map((li, i) => (
                <KV key={i} k={li.quantity > 1 ? `${li.name} × ${li.quantity}` : li.name}>
                  <span className="tnum">{formatUsd(li.amount_usd_cents)}</span>
                </KV>
              ))}
              {invoice.client_reference_id && <KV k="Your reference" mono>{invoice.client_reference_id}</KV>}
              {buyerAnswers(invoice.metadata).map((f, i) => (
                <KV key={`cf${i}`} k={f.label}>{f.value}</KV>
              ))}
              {invoice.customer_email && <KV k="Email">{invoice.customer_email}</KV>}
            </div>
          </>
        )}

        {invoice.shipping && (
          <>
            <div className="s-label">Ship to</div>
            <div className="card card-pad" style={{ fontSize: 14, lineHeight: 1.55, whiteSpace: "pre-line" }}>
              {[
                invoice.shipping.name,
                invoice.shipping.line1,
                invoice.shipping.line2,
                [invoice.shipping.postal_code, invoice.shipping.city].filter(Boolean).join(" "),
                invoice.shipping.state,
                invoice.shipping.country,
              ]
                .filter(Boolean)
                .join("\n")}
            </div>
          </>
        )}

        <div className="s-label">Details</div>
        <div className="card card-pad">
          <KV k="Link id" mono>{truncMid(invoice.id, 8, 6)}</KV>
          <KV k="Settle to">
            {invoice.settlement.chain} USDC
          </KV>
          <KV k="Address" mono>{truncMid(invoice.settlement.address, 8, 6)}</KV>
          <KV k="Created">{formatDateTime(invoice.created_at)}</KV>
          <KV k="Expires">{formatDateTime(invoice.expires_at)}</KV>
          {invoice.paid_at && (
            <KV k="Paid">{formatDateTime(invoice.paid_at)}</KV>
          )}
          <KV k="Mode">{invoice.mode}</KV>
        </div>

        {invoice.payment_targets && invoice.payment_targets.length > 0 && (
          <>
            {/*
              V1.4.4 — once an invoice is paid, the "Buyer can pay on"
              list (all 12 targets) is no longer the relevant view: the
              buyer already picked one. Show only the paid target with a
              "Paid via" header in that case; the rest become noise.
              Pending invoices keep the full list (it's the picker preview).
            */}
            {invoice.status === "paid" && invoice.paid_on ? (
              <>
                <div className="s-label">Paid via</div>
                <div className="card card-pad">
                  {(() => {
                    const paidChain = invoice.paid_on.chain_id;
                    const paidAsset = invoice.paid_on.asset_symbol;
                    const t = invoice.payment_targets.find(
                      (x) =>
                        x.chain_id === paidChain &&
                        (x.asset_symbol ?? null) === (paidAsset ?? null),
                    ) ?? invoice.payment_targets.find((x) => x.chain_id === paidChain);
                    if (!t) {
                      return (
                        <KV k={CHAIN_LABEL[paidChain] ?? paidChain}>
                          <span className="tnum">{invoice.paid_on.tx_hash ? truncMid(invoice.paid_on.tx_hash, 10, 8) : "—"}</span>
                        </KV>
                      );
                    }
                    const baseLabel = CHAIN_LABEL[t.chain_id] ?? t.chain_id;
                    const label =
                      t.asset_symbol === "USDT"
                        ? `${baseLabel.replace(" USDC", "")} · USDT`
                        : baseLabel;
                    const ticker =
                      t.asset_symbol === "USDT"
                        ? "USDT"
                        : t.asset_symbol === "USDC"
                          ? "USDC"
                          : CHAIN_TICKER[t.chain_id] ?? "";
                    return (
                      <>
                        <KV k={label}>
                          <span className="tnum">
                            {formatChainAmount(t.amount_atomic, t.chain_id)} {ticker}
                          </span>
                        </KV>
                        <KV k="Address" mono>{truncMid(t.address, 8, 6)}</KV>
                        {invoice.paid_on.tx_hash && (
                          <KV k="Tx hash" mono>{truncMid(invoice.paid_on.tx_hash, 8, 6)}</KV>
                        )}
                      </>
                    );
                  })()}
                </div>
              </>
            ) : (
              <>
                <div className="s-label">Buyer can pay on</div>
                <div className="card card-pad">
                  {invoice.payment_targets.map((t, idx) => {
                    // V1.3: EVM rows now disambiguate USDC vs USDT.
                    const baseLabel = CHAIN_LABEL[t.chain_id] ?? t.chain_id;
                    const label =
                      t.asset_symbol === "USDT"
                        ? `${baseLabel.replace(" USDC", "")} · USDT`
                        : baseLabel;
                    const ticker =
                      t.asset_symbol === "USDT"
                        ? "USDT"
                        : t.asset_symbol === "USDC"
                          ? "USDC"
                          : CHAIN_TICKER[t.chain_id] ?? "";
                    return (
                      <KV
                        key={`${t.chain_id}-${t.asset_symbol ?? ""}-${idx}`}
                        k={label}
                      >
                        <span className="tnum">
                          {formatChainAmount(t.amount_atomic, t.chain_id)} {ticker}
                        </span>
                      </KV>
                    );
                  })}
                </div>
              </>
            )}
          </>
        )}

        <div className="s-label">Actions</div>
        <div style={{ display: "grid", gap: 8 }}>
          <Btn
            variant="secondary"
            onClick={() => void handleCopy()}
          >
            {copied ? (
              <><Ic name="check" size={16} /> Copied</>
            ) : (
              <><Ic name="copy" size={16} /> Copy payment URL</>
            )}
          </Btn>
          <Btn variant="secondary" onClick={handleShare}>
            <Ic name="share" size={16} /> Share via Telegram
          </Btn>
          <a
            href={invoice.pay_url}
            target="_blank"
            rel="noreferrer"
            className="btn btn-ghost"
          >
            <Ic name="ext" size={16} /> Open hosted checkout
          </a>
        </div>
      </div>
    </div>
  );
}
